// components/about/VideoHero.js
import React, { useState, useRef, useEffect } from 'react';

const VideoHero = ({ videoSrc, title, description }) => {
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [hasVideoError, setHasVideoError] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const videoRef = useRef(null);
  const sectionRef = useRef(null);

  // Set up intersection observer to detect when component enters viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  // Handle video loaded event
  const handleVideoLoaded = () => {
    setIsVideoLoaded(true);
  };

  const handleVideoError = () => {
    setHasVideoError(true);
  };

  return (
    <section 
      ref={sectionRef}
      className="relative mb-0 border-b border-gray-200 min-h-screen flex items-center justify-center"
    >
      {/* Video Background with Lazy Loading */}
      <div className="absolute inset-0 z-0">
        {/* Loading placeholder - shown while video loads */}
        {(!isVideoLoaded || !isInView) && !hasVideoError && (
          <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
            <div className="w-20 h-20 border-t-4 border-b-4 border-red-600 rounded-full animate-spin"></div>
          </div>
        )}
        
        {/* Only render video when in viewport */}
        {isInView && (
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            className={`w-full h-full object-cover transition-opacity duration-700 ${isVideoLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoadedData={handleVideoLoaded}
            onError={handleVideoError}
            preload="metadata"
          >
            <source src={videoSrc} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        )}
        
        <div className="absolute inset-0 bg-black opacity-60"></div>
      </div>

      {/* Text Container - Centered */}
      <div className="relative z-10 text-center px-6 md:px-12">
        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 md:mb-8">
          {title}
        </h1>
        <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto">
          {description}
        </p>
      </div>
    </section>
  );
};

export default VideoHero;